/**
 * The speaker rail's filter, and only its filter, for a phone.
 *
 * `SpeakerRail` does three jobs at once — legend, talk-share summary, and
 * filter — and on a 375px screen that costs about 260px of vertical space
 * before the transcript starts. Two of the three jobs already have a better
 * home on a phone: the legend and the summary are the "Who did the talking"
 * bars in the Insights tab, drawn from the same roster and the same colours.
 * The filter is the one job with nowhere else to go, and it is the one people
 * reach for while reading, so it is the one that stays.
 *
 * A horizontally scrolling row rather than a wrapping one. Eight participants
 * is the API's ceiling (FINDINGS.md #2), and eight chips wrapped at 375px is
 * three rows that push the transcript down by ~110px permanently; scrolled, it
 * is one 40px row whether there are two people or eight. The horizontal
 * scroller is safe next to the app's single vertical one — `overscroll-x-contain`
 * stops a horizontal fling from turning into a back-navigation gesture, and
 * nothing here scrolls vertically, so there is no nested vertical trap.
 */

import { Monogram } from './monogram'
import { cn } from '@/lib/utils'
import type { SpeakerRoster } from '../speakers'

export function SpeakerChips({
  roster,
  focusIdentity,
  onFocus,
  className,
}: {
  roster: SpeakerRoster
  focusIdentity: string | null
  onFocus: (identity: string | null) => void
  className?: string
}) {
  // Nothing to filter to. A one-person row is 40px of a phone viewport spent on
  // a control whose only outcome is "show the turns you are already looking
  // at", and one-speaker sessions are the common case in testing.
  if (roster.list.length < 2) return null

  return (
    <div
      className={cn(
        // Bleeds to both screen edges so the row reads as a scrollable strip
        // rather than a clipped list, and so the first chip is not inset from
        // everything above it.
        // `-my-1 py-1` buys the 2px above and below that `tap44` needs to
        // centre a 44px touch box on a 40px chip. It has to be padding rather
        // than nothing at all because this row scrolls: `overflow-x: auto`
        // computes `overflow-y` to `auto`, which would clip the touch box out
        // of existence. The equal negative margin returns the space, so the
        // strip is the same height it was.
        '-mx-4 -my-1 overflow-x-auto overscroll-x-contain px-4 py-1',
        className,
      )}
    >
      <div className="flex w-max items-center gap-2">
        {roster.list.map((speaker) => {
          const selected = focusIdentity === speaker.identity
          return (
            <button
              key={speaker.identity}
              type="button"
              onClick={() => onFocus(selected ? null : speaker.identity)}
              aria-pressed={selected}
              className={cn(
                // 40px tall as PAINTED, which is the height the row wants; the
                // touch box is taken to 44 by `tap44` without the chip growing.
                // The width — a monogram plus a name — was already well past 44
                // in the other axis.
                'tap44 inline-flex h-10 shrink-0 items-center gap-2 rounded-full border pr-3.5 pl-1.5',
                'text-[0.8125rem] font-medium whitespace-nowrap transition-colors',
                // The tap highlight is off globally, so press feedback is this
                // component's own job — see the tab bar in shell.tsx.
                'active:bg-surface-3',
                selected
                  ? 'border-accent-line bg-accent-soft text-accent'
                  : 'border-line-strong bg-surface text-ink-muted',
              )}
            >
              <Monogram displayName={speaker.displayName} index={speaker.index} size="sm" />
              <span className="max-w-[8rem] truncate">{speaker.displayName}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
